import classNames from 'classnames';
import React, { ChangeEvent, useEffect, useState } from 'react';
import AvatarEditor from 'react-avatar-editor';
import Dropzone from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import Notifications from 'react-notification-system-redux';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import InlineSpinner from '../../../common/components/misc/InlineSpinner';
import * as Actions from '../../../common/services/customer/Actions';
import * as CustomerService from '../../../common/services/customer/Service';
import { CustomerError } from '../../../common/services/customer/Types';
import { BinaryFileWithContentDTO } from '../../../common/types/dto/BinaryFileWithContentDTO';
import { CustomerDataDTO } from '../../../common/types/dto/CustomerDataDTO';
import lapisEditar from '../../assets/images/icons/Cinzentos/lapisEditar.svg';
import defaultPic from '../../assets/images/placeholder-profile-picture-default-light.jpg';
import { RootState } from '../../base/RootReducer';
import { config } from '../../common/';
import { AppThunkDispatch } from '../../common/types';

interface ReduxStateProps {
    isCommunicatingPUT: boolean;
    isCommunicatingGET: boolean;
    customerInfo: CustomerDataDTO | undefined;
}

interface ReduxDispatchProps {
    customerActions: typeof Actions;
    customerServiceActions: CustomerService.ServiceActions;
    dispatch: AppThunkDispatch;
}

Modal.setAppElement('#root');

const Avatar: React.FC<ReduxStateProps & ReduxDispatchProps> = (props) => {
    const [showModal, setShowModal] = useState(false);
    const [submitErrorText, setSubmitErrorText] = useState('');
    const [isNewFileSet, setIsNewFileSet] = useState(false);
    const [degrees, setDegrees] = useState(0);
    const [scale, setScale] = useState(1);
    const [avatarEditorRef, setAvatarEditorRef] = useState<any>(null);
    const [profilePicture, setProfilePicture] = useState<string | undefined>(undefined);
    const [isBase64, setIsBase64] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const { t } = useTranslation();

    const loadImageFromProps = () => {
        if (props.customerInfo) {
            if (props.customerInfo.profilePicture) {
                setProfilePicture(props.customerInfo.profilePicture);
                setIsBase64(true);
            } else {
                setProfilePicture(defaultPic);
                setIsBase64(false);
            }
        }
    };

    const onCloseModal = (e: React.MouseEvent<{}> | null) => {
        if (e) {
            e.preventDefault();
        }
        setShowModal(false);
        setSubmitErrorText('');
        setIsNewFileSet(false);
        setDegrees(0);
        setScale(1);
        setFile(null);
        loadImageFromProps();
    };

    const onBlob = (blob: Blob | null) => {
        if (blob) {
            const data: BinaryFileWithContentDTO = {
                content: blob,
                fileExt: '.jpg',
                fileName: 'profile.jpg',
            };

            props.customerServiceActions.uploadProfilePicture(data)
                .then(
                    (result: any) => {
                        props.dispatch(Notifications.success({
                            title: t('success'),
                            message: t('profile:profilePicture.success'),
                            position: 'tc',
                            autoDismiss: 5,
                        }));
                        onCloseModal(null);
                        props.customerServiceActions.customerData();
                    },
                    (reason: any) => {
                        if (reason instanceof CustomerError) {
                            const exceptionType: string = reason.customerErrorInfo.exceptionType || 'UnknownException';
                            const exceptionErrorMsg: string = t(`exception:${exceptionType.split('.').pop()}`);
                            setSubmitErrorText(exceptionErrorMsg);
                            props.dispatch(Notifications.error({
                                title: t('error'),
                                message: exceptionErrorMsg,
                                position: 'tc',
                                autoDismiss: 5,
                            }));
                        } else {
                            throw reason;
                        }
                    },
                );
        }
    };

    const onSubmit = async (e: React.MouseEvent<{}> | null) => {
        if (e) {
            e.preventDefault();
        }
        if (!file || props.isCommunicatingPUT) {
            return;
        }

        if (avatarEditorRef) {
            // This returns a HTMLCanvasElement, it can be made into a data URL or a blob,
            // drawn on another canvas, or added to the DOM.
            // const canvas: HTMLCanvasElement = this.state.avatarEditorRef.getImage();
            const canvas = avatarEditorRef.getImageScaledToCanvas();
            canvas.toBlob(onBlob, 'image/jpeg');
        }
    };

    const modalParentSelector = () => {
        return document.getElementById(config.BrandEnv) ? document.getElementById(config.BrandEnv) as HTMLElement : document.body;
    };

    const onSetAvatarEditorRef = async (editor: any) => {
        // if (!avatarEditorRef) {
        setAvatarEditorRef(editor);
        // }
    };

    const onClickLeft = async (e: React.MouseEvent<{}> | null) => {
        if (e) {
            e.preventDefault();
        }
        setDegrees(degrees - 90);
    };

    const onClickRight = async (e: React.MouseEvent<{}> | null) => {
        if (e) {
            e.preventDefault();
        }
        setDegrees(degrees + 90);
    };

    const onScaleChange = async (event: ChangeEvent<HTMLInputElement>) => {
        setScale(parseFloat(event.target.value));
    };

    const handleImageChange = (accepted: File[]) => {
        if (accepted && accepted[0]) { // Accepted list has 1 element (multiple = false)
            const imgfile = accepted[0];
            const previewUrl = URL.createObjectURL(imgfile);
            setProfilePicture(previewUrl);
            setIsBase64(false);
            setIsNewFileSet(true);
            setFile(imgfile);
        }
    };

    useEffect(() => {
        loadImageFromProps();
    }, []);

    useEffect(() => {
        // Cleanup the object URL when the component unmounts
        return () => {
            if (profilePicture) {
                URL.revokeObjectURL(profilePicture);
            }
        };
    }, [profilePicture]);

    const renderModal = () => {
        if (props.isCommunicatingGET || props.isCommunicatingPUT) {
            return (<InlineSpinner size={'100px'} />);
        } else {
            return (
                <React.Fragment>
                    <h2 className={'margin-bottom-2'}>{t('profile:profilePicture.label')}</h2>
                    {isNewFileSet &&
                        <React.Fragment>
                            <div className={classNames(['grid-x', 'align-center'])}>
                                <AvatarEditor
                                    ref={(editor: any) => (onSetAvatarEditorRef(editor))}
                                    image={(isBase64 ? 'data:image/jpeg;base64, ' : '') + profilePicture}
                                    width={200}
                                    height={200}
                                    borderRadius={100}
                                    rotate={degrees}
                                    scale={scale}
                                />
                            </div>
                            <div className={classNames(['grid-x', 'grid-margin-x', 'grid-margin-y', 'align-middle', 'margin-top-15'])}>
                                <div className={classNames(['cell', 'cell-centered', 'medium-12', 'small-12'])}>
                                    <div className={'avatar-slider'}>
                                        <div className={'minus'} />
                                        <input key="sliderBtn" type="range" min="1" max="3" step="0.1" value={scale} onChange={onScaleChange} />
                                        <div className={'plus'} />
                                    </div>
                                </div>
                                <div className={classNames(['cell', 'cell-centered', 'medium-12', 'small-12'])}>
                                    <div className={classNames(['button-group', 'align-center', 'margin-0'])}>
                                        <button key="leftBtn" onClick={onClickLeft} className={classNames(['button', 'bttSubscrever', 'button-svg'])}><svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 512 512"><path fill="#000000" d="M48.5 224H40c-13.3 0-24-10.7-24-24V72c0-9.7 5.8-18.5 14.8-22.2s19.3-1.7 26.2 5.2L98.6 96.6c87.6-86.5 228.7-86.2 315.8 1c87.5 87.5 87.5 229.3 0 316.8s-229.3 87.5-316.8 0c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0c62.5 62.5 163.8 62.5 226.3 0s62.5-163.8 0-226.3c-62.2-62.2-162.7-62.5-225.3-1L185 183c6.9 6.9 8.9 17.2 5.2 26.2s-12.5 14.8-22.2 14.8H48.5z" /></svg></button>
                                        <button key="rightBtn" onClick={onClickRight} className={classNames(['button', 'bttSubscrever', 'button-svg'])}><svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 512 512"><path fill="#000000" d="M386.3 160H336c-17.7 0-32 14.3-32 32s14.3 32 32 32H464c17.7 0 32-14.3 32-32V64c0-17.7-14.3-32-32-32s-32 14.3-32 32v51.2L414.4 97.6c-87.5-87.5-229.3-87.5-316.8 0s-87.5 229.3 0 316.8s229.3 87.5 316.8 0c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0c-62.5 62.5-163.8 62.5-226.3 0s-62.5-163.8 0-226.3s163.8-62.5 226.3 0L386.3 160z" /></svg></button>
                                    </div>
                                </div>
                            </div>
                            <div className={'avatar-dropzone'}>
                                <p className={classNames(['margin-top-1', 'margin-bottom-0'])}>{t('profile:dropzone.label3')}</p>
                            </div>
                        </React.Fragment>
                    }
                    {!isNewFileSet &&
                        <div className={'avatar-dropzone'}>
                            <Dropzone
                                onDrop={handleImageChange}
                                multiple={false}
                                accept={{ 'image/jpeg': ['.jpeg', '.jpg'] }}
                                minSize={undefined}
                                maxSize={5000000}
                            >
                                {({ getRootProps, getInputProps, isDragActive, isDragReject }) => (
                                    <div {...getRootProps()}>
                                        <input {...getInputProps()} />
                                        <div
                                            className={classNames(['profile-header-avatar-img'])}
                                            style={{ backgroundImage: `url("${defaultPic}")` }}
                                        />
                                    </div>
                                )}
                            </Dropzone>
                            <p className={classNames(['margin-bottom-5', 'margin-top-1'])}>{t('profile:dropzone.label1')}</p>
                            <p>{t('profile:dropzone.label2')}</p>
                        </div>
                    }
                    <div className="error-message">{submitErrorText}</div>
                    <div className={classNames(['button-group', 'align-center'])}>
                        <button type="button" className={classNames(['button', 'bttSubscrever'])} disabled={props.isCommunicatingPUT || !isNewFileSet} onClick={onSubmit}>{props.isCommunicatingPUT ? t('loadingText') : t('profile:profilePicture.upload')}</button>
                        <button type="button" className={classNames(['button', 'bttCancelar'])} onClick={onCloseModal}>{t('cancel')}</button>
                    </div>
                </React.Fragment>
            );
        }
    };

    return (
        <React.Fragment>
            <div
                className={classNames(['profile-header-avatar-img'])}
                style={{ backgroundImage: `url("${isBase64 ? 'data:image/jpeg;base64, ' : ''}${profilePicture}")` }}
            />
            <div role="button" className={'profile-header-avatar-edit'} onClick={() => setShowModal(true)}>
                <img src={lapisEditar} />
            </div>
            <Modal isOpen={showModal} parentSelector={modalParentSelector} overlayClassName={'modal-overlay'} className={'modal-content'}>
                {renderModal()}
            </Modal>
        </React.Fragment>
    );
};

const mapStateToProps = (store: RootState) => ({
    isCommunicatingPUT: store.customerState.uploadProfilePicture.isCommunicating,
    isCommunicatingGET: store.customerState.customerData.isCommunicating,
    customerInfo: store.customerState.customerData.data.customerData,
});

const mapDispatchToProps = (dispatch: AppThunkDispatch) => ({
    customerActions: bindActionCreators(Actions, dispatch),
    customerServiceActions: CustomerService.bindServiceActionCreators(config, dispatch),
    dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(Avatar);
